/**
 * Capacitor (Android) bring-up: splash, keyboard, status bar, deep-link
 * OIDC return path, and Stripe-checkout return path. No-ops on the web build.
 *
 * The native OIDC flow:
 *   1. `login.tsx` detects native and calls `Browser.open(api/auth/oauth/login?return_to=de.overfit.logbuch://oauth-callback)`.
 *   2. Keycloak → backend callback → backend redirects to the custom scheme
 *      with tokens in the URL fragment.
 *   3. Android's intent-filter on `de.overfit.logbuch` re-opens the app, firing
 *      `appUrlOpen` which we handle here.
 *
 * The native billing flow:
 *   1. `billing.tsx` / `setup.tsx` detect native and call
 *      `billing.checkout(plan, "stripe", { returnToNative: true })`, then
 *      `Browser.open(redirect_url)`.
 *   2. Backend passes `de.overfit.logbuch://billing/success` (or `/cancel`)
 *      to Stripe.
 *   3. Stripe sends webhook to user-server-v2 → user marked paid.
 *   4. Stripe bounces the user back to the custom scheme → Android opens our
 *      app → `appUrlOpen` here closes the browser, busts the billing cache,
 *      and broadcasts `billing:refresh` so the paywall UI re-fetches.
 *
 * Defence in depth: if Stripe never fires the deep-link (user closes browser
 * manually mid-flow), the `appStateChange` listener below catches the resume
 * and refreshes once when a checkout was in flight.
 */
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Keyboard } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { auth, billing } from "../api/client";

export const IS_NATIVE = Capacitor.isNativePlatform();

export const NATIVE_OIDC_RETURN_TO = "de.overfit.logbuch://oauth-callback";
const NATIVE_BILLING_PREFIX = "de.overfit.logbuch://billing/";

const PENDING_CHECKOUT_KEY = "billing:checkout-pending";

/** Externe Seite öffnen: nativ im System-Browser, im Web in neuem Tab. */
export function openExternal(url: string): void {
  if (IS_NATIVE) {
    void Browser.open({ url });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function markBillingCheckoutPending(): void {
  try {
    sessionStorage.setItem(PENDING_CHECKOUT_KEY, String(Date.now()));
  } catch {
    // sessionStorage can throw in private-mode WebViews — non-fatal.
  }
}

function clearBillingCheckoutPending(): void {
  try {
    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // ignore
  }
}

function hasPendingCheckout(): boolean {
  try {
    return sessionStorage.getItem(PENDING_CHECKOUT_KEY) != null;
  } catch {
    return false;
  }
}

async function refreshAndBroadcast(reason: string): Promise<void> {
  clearBillingCheckoutPending();
  try {
    await billing.refresh();
  } catch {
    // refresh may 502 if user-server-v2 is briefly unreachable — the
    // listener fires again on the next resume.
  }
  window.dispatchEvent(new CustomEvent("billing:refresh", { detail: { reason } }));
}

let initialized = false;

export function initNative(): void {
  if (!IS_NATIVE || initialized) return;
  initialized = true;

  // Splash is configured to auto-hide via capacitor.config.ts, but call it
  // explicitly once the first paint settles so we don't leave it up longer
  // than necessary on faster devices.
  requestAnimationFrame(() => {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
  });

  // Keyboard: keep the WebView's viewport stable when the soft keyboard opens.
  Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});

  // Deep-link handler: OIDC callback + Stripe billing return.
  CapApp.addListener("appUrlOpen", async (event) => {
    const url = event?.url || "";
    if (!url.startsWith("de.overfit.logbuch://")) return;

    if (url.startsWith(NATIVE_BILLING_PREFIX)) {
      Browser.close().catch(() => {});
      await refreshAndBroadcast("deeplink");
      return;
    }

    const consumed = auth.consumeOidcFragment(url);
    Browser.close().catch(() => {});
    if (consumed) {
      // Bounce to root so any route guards re-run with a valid token in
      // localStorage. Using location.assign (not history.push) because the
      // app may have been re-launched and React Router won't be in scope.
      window.location.assign("/");
    } else {
      window.location.assign("/login");
    }
  });

  // Resume listener: if the user closes Stripe in the browser without
  // completing (or Stripe fails to fire the deep-link), the OS still wakes
  // the app. Refresh the billing status once so the paywall reflects truth.
  CapApp.addListener("appStateChange", async ({ isActive }) => {
    if (!isActive) return;
    if (!hasPendingCheckout()) return;
    await refreshAndBroadcast("resume");
  });
}
